/**
 * 🎨 IMAGE FLOATING TOOLBAR 2025
 * 
 * Floating Toolbar für Image-Element
 * - Upload Tab: Neues Bild hochladen
 * - Sizing Tab: Width, Height, Max-Width
 * - Styling Tab: Border Radius, Border, Shadow, Object-Fit
 * - Spacing Tab: Margins, Alignment
 */

import React, { useState, useRef, useEffect } from 'react';
import ReactDOM from 'react-dom';
import './ImageFloatingToolbar.css';
import './TipTapTextAnimations.css';

function ImageFloatingToolbar({
    element,
    onClose,
    onStyleChange,
    onImageUpload,
    colors = {},
    anchorRef,
    isMobileLayout = false,
    isUploading = false
}) {
    const [activeTab, setActiveTab] = useState('upload'); // ✅ Upload als Standard
    const [isDragging, setIsDragging] = useState(false);
    const [device, setDevice] = useState(isMobileLayout ? 'mobile' : 'desktop'); // ✅ Desktop/Mobile Toggle
    const [toolbarPosition, setToolbarPosition] = useState({ top: 80, left: window.innerWidth / 2 }); // ✅ Fixed Position
    const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
    const [isManuallyPositioned, setIsManuallyPositioned] = useState(false);
    const toolbarRef = useRef(null);
    
    // ✅ Synchronisiere Device Toggle mit isMobileLayout
    useEffect(() => {
        setDevice(isMobileLayout ? 'mobile' : 'desktop');
    }, [isMobileLayout]);
    
    // 🎯 Drag Handlers
    const handleMouseDown = (e) => {
        // Verhindere Drag wenn auf Tab-Button oder Close geklickt
        if (e.target.closest('.image-tab') || e.target.closest('.image-toolbar-close')) {
            return;
        }
        
        e.preventDefault();
        e.stopPropagation();
        
        setIsDragging(true);
        setDragOffset({
            x: e.clientX - toolbarPosition.left,
            y: e.clientY - toolbarPosition.top
        });
    };
    
    const handleMouseMove = (e) => {
        if (!isDragging) return;
        
        const newLeft = e.clientX - dragOffset.x;
        const newTop = e.clientY - dragOffset.y;
        
        // Bounds checking - Toolbar bleibt im Viewport
        const maxLeft = window.innerWidth - (toolbarRef.current?.offsetWidth || 400);
        const maxTop = window.innerHeight - (toolbarRef.current?.offsetHeight || 300);
        
        setToolbarPosition({
            left: Math.max(0, Math.min(newLeft, maxLeft)),
            top: Math.max(0, Math.min(newTop, maxTop))
        });
    };
    
    const handleMouseUp = () => {
        setIsDragging(false);
        setIsManuallyPositioned(true);
    };
    
    // ✅ Drag Event Listeners
    useEffect(() => {
        if (isDragging) {
            document.addEventListener('mousemove', handleMouseMove);
            document.addEventListener('mouseup', handleMouseUp);
            
            return () => {
                document.removeEventListener('mousemove', handleMouseMove);
                document.removeEventListener('mouseup', handleMouseUp);
            };
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isDragging]);
    
    // ✅ Handle Image Upload - DYNAMIC FILE INPUT (wie Gallery)
    const handleImageUploadClick = () => {
        console.log('🖱️ Upload button clicked');
        
        // ✅ Erstelle dynamisch ein File Input (wie in Gallery)
        const input = document.createElement('input');
        input.type = 'file';
        input.accept = 'image/*';
        
        input.onchange = (e) => {
            const file = e.target.files[0];
            console.log('📁 File selected:', file?.name);
            if (file) {
                onImageUpload(file);
            }
        };
        
        // ✅ DIREKT ÖFFNEN - funktioniert immer!
        input.click();
        console.log('✅ File picker opened');
    };
    
    // ✅ Drag & Drop Handlers
    const handleDragEnter = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(true);
    };
    
    const handleDragLeave = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(false);
    };
    
    const handleDragOver = (e) => {
        e.preventDefault();
        e.stopPropagation();
    };
    
    const handleDrop = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(false);
        
        const files = e.dataTransfer.files;
        if (files && files.length > 0) {
            const file = files[0];
            if (file.type.startsWith('image/')) {
                console.log('📦 File dropped:', file.name);
                onImageUpload(file);
            } else {
                alert('Please drop an image file.');
            }
        }
    };

    // ✅ Close on outside click - FIXED (wie Button Toolbar)
    useEffect(() => {
        const handleClickOutside = (event) => {
            // ✅ CRITICAL FIX: Check via closest() - funktioniert auch nach Re-Renders!
            const clickedInToolbar = event.target.closest('.image-floating-toolbar-portal');
            const clickedInAnchor = anchorRef && anchorRef.current && anchorRef.current.contains(event.target);
            
            // Close only if clicked OUTSIDE of toolbar AND anchor
            if (!clickedInToolbar && !clickedInAnchor) {
                onClose();
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [onClose, anchorRef]);

    // ✅ Close on Escape
    useEffect(() => {
        const handleEscape = (e) => {
            if (e.key === 'Escape') {
                onClose();
            }
        };

        document.addEventListener('keydown', handleEscape);
        return () => {
            document.removeEventListener('keydown', handleEscape);
        };
    }, [onClose]);

    // ✅ Current Values - basierend auf Device Toggle (nicht isMobileLayout)
    const currentWidth = device === 'mobile' ? 
        (element.widthMobile || element.width || 300) : 
        (element.width || 400);
    const currentHeight = device === 'mobile' ? 
        (element.heightMobile || element.height || 200) : 
        (element.height || 300);
    const currentMaxWidth = device === 'mobile' ?
        (element.maxWidthMobile || element.maxWidth || 600) :
        (element.maxWidth || 800);
    const currentBorderRadius = device === 'mobile' ?
        (element.borderRadiusMobile ?? element.borderRadius ?? 8) :
        (element.borderRadius ?? 8);

    // ✅ PORTAL RENDERING - Wie Button Toolbar
    const renderToolbar = () => (
        <>
            <div 
                className={`image-floating-toolbar-portal ${activeTab ? 'expanded' : ''} ${isDragging ? 'dragging' : ''}`}
                ref={toolbarRef}
                style={{
                    position: 'fixed',
                    top: `${toolbarPosition.top}px`,
                    left: `${toolbarPosition.left}px`,
                    transform: 'translateX(-50%)',
                    zIndex: 999999
                }}
                onMouseDown={(e) => {
                    // ✅ CRITICAL FIX: Stoppe ALLE mousedown Events auf Portal-Level
                    e.stopPropagation();
                }}
                onClick={(e) => {
                    // ✅ CRITICAL FIX: Stoppe ALLE click Events auf Portal-Level
                    e.stopPropagation();
                }}
            >
                <div className="image-toolbar">
                {/* 🎨 MAIN TOOLBAR BUTTONS - oben fixiert */}
                <div className="image-button-row">
                    {/* 🎯 Drag Handle Icon - Links */}
                    <div 
                        className="image-drag-handle"
                        style={{ cursor: isDragging ? 'grabbing' : 'grab' }}
                        title="Drag to move toolbar"
                        onMouseDown={handleMouseDown}
                        onClick={(e) => {
                            // ✅ FIX: Verhindere dass Click-Event die Toolbar schließt
                            e.preventDefault();
                            e.stopPropagation();
                        }}
                    >
                        <i className="fas fa-grip-vertical"></i>
                    </div>
                    
                    {/* ✅ Icons-Gruppe - Mittig zentriert */}
                    <div className="image-button-group-center">
                        {/* 1. Upload */}
                        <button
                            onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                setActiveTab(activeTab === 'upload' ? null : 'upload');
                            }}
                            className={`image-toolbar-btn ${activeTab === 'upload' ? 'is-active' : ''}`}
                            title="Upload Image"
                        >
                            <i className="fas fa-cloud-upload-alt"></i>
                        </button>
                        
                        {/* 2. Presets */}
                        <button
                            onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                setActiveTab(activeTab === 'presets' ? null : 'presets');
                            }}
                            className={`image-toolbar-btn ${activeTab === 'presets' ? 'is-active' : ''}`}
                            title="Image Presets (Square, Circle, 4:3, 16:9, etc.)"
                        >
                            <i className="fas fa-shapes"></i>
                        </button>
                        
                        {/* 3. Sizing */}
                        <button
                            onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                setActiveTab(activeTab === 'sizing' ? null : 'sizing');
                            }}
                            className={`image-toolbar-btn ${activeTab === 'sizing' ? 'is-active' : ''}`}
                            title="Image Sizing (Width, Height, Max-Width)"
                        >
                            <i className="fas fa-expand-arrows-alt"></i>
                        </button>
                        
                        {/* 4. Spacing */}
                        <button
                            onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                setActiveTab(activeTab === 'spacing' ? null : 'spacing');
                            }}
                            className={`image-toolbar-btn ${activeTab === 'spacing' ? 'is-active' : ''}`}
                            title="Image Spacing (Margins, Alignment)"
                        >
                            <i className="fas fa-arrows-alt-v"></i>
                        </button>
                        <button
                        onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            setActiveTab(activeTab === 'styling' ? null : 'styling');
                        }}
                        className={`image-toolbar-btn ${activeTab === 'styling' ? 'is-active' : ''} ${(element.borderEnabled || element.shadowEnabled) ? 'has-feature' : ''}`}
                        title="Image Styling (Border, Shadow, Effects)"
                    >
                        <i className="fas fa-magic"></i>
                    </button>
                    
                    {/* 6. Animation */}
                    <button
                        onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            setActiveTab(activeTab === 'animation' ? null : 'animation');
                        }}
                        className={`image-toolbar-btn ${activeTab === 'animation' ? 'is-active' : ''} ${element.animationEnabled ? 'has-feature' : ''}`}
                        title="Animation"
                    >
                        <i className="fas fa-play-circle"></i>
                    </button>
                    </div>
                    
                    {/* 5. Styling - Ganz rechts */}
            
                </div>
                
                {/* 📦 EXPANDABLE SECTIONS - Klappen nach unten auf */}
                
                {/* 1️⃣ UPLOAD SECTION */}
                {activeTab === 'upload' && (
                    <div className="image-expanded-section">
                        {/* Current Image Preview */}
                        {element.imageUrl && (
                            <div className="image-current-preview">
                                <img src={element.imageUrl} alt="Current" />
                            </div>
                        )}

                        {/* Drag & Drop Zone - Clean & Clickable */}
                        <div 
                            className={`image-drop-zone ${isDragging ? 'dragging' : ''}`}
                            onClick={(e) => {
                                e.stopPropagation(); // ✅ Verhindert Toolbar-Close
                                console.log('🎯 Drop Zone clicked!');
                                // ✅ Öffne File Picker mit dynamischem Input
                                handleImageUploadClick();
                            }}
                            onDragEnter={(e) => {
                                e.stopPropagation();
                                handleDragEnter(e);
                            }}
                            onDragLeave={(e) => {
                                e.stopPropagation();
                                handleDragLeave(e);
                            }}
                            onDragOver={(e) => {
                                e.stopPropagation();
                                handleDragOver(e);
                            }}
                            onDrop={(e) => {
                                e.stopPropagation();
                                handleDrop(e);
                            }}
                        >
                            <div className="image-drop-zone-content">
                                <i className="fas fa-cloud-upload-alt"></i>
                                <p className="image-drop-zone-title">
                                    {isDragging ? 'Drop image here' : 'Click or drag & drop image'}
                                </p>
                                <p className="image-drop-zone-info">Max file size: 2MB • JPG, PNG, GIF, WEBP</p>
                            </div>
                        </div>

                        {/* Alt Text */}
                        <div className="image-control-group">
                            <label className="image-control-label">
                                <i className="fas fa-tag"></i>
                                Alt Text
                            </label>
                            <input
                                type="text"
                                value={element.altText || ''}
                                onChange={(e) => onStyleChange({ altText: e.target.value })}
                                onClick={(e) => e.stopPropagation()} // ✅ Verhindert Toolbar-Close
                                className="image-text-input"
                                placeholder="Describe this image..."
                            />
                        </div>
                        
                        <span className="image-section-label">Upload</span>
                    </div>
                )}

                {/* 2️⃣ PRESETS SECTION */}
                {activeTab === 'presets' && (
                    <div className="image-expanded-section">
                        <div className="image-presets-grid">
                            {/* Square */}
                            <button
                                className="image-preset-btn"
                                onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    onStyleChange({
                                        width: 400,
                                        height: 400,
                                        widthMobile: 300,
                                        heightMobile: 300,
                                        widthType: 'pixel',
                                        heightType: 'pixel',
                                        borderRadius: 8,
                                        borderRadiusMobile: 8,
                                        objectFit: 'cover'
                                    });
                                }}
                                title="Square (400x400px)"
                            >
                                <div className="image-preset-icon">⬜</div>
                                <div className="image-preset-name">Square</div>
                                <div className="image-preset-desc">400x400px</div>
                            </button>
                            
                            {/* Circle */}
                            <button
                                className="image-preset-btn"
                                onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    onStyleChange({
                                        width: 300,
                                        height: 300,
                                        widthMobile: 250,
                                        heightMobile: 250,
                                        widthType: 'pixel',
                                        heightType: 'pixel',
                                        borderRadius: 500,
                                        borderRadiusMobile: 500,
                                        objectFit: 'cover'
                                    });
                                }}
                                title="Circle (300x300px)"
                            >
                                <div className="image-preset-icon">⭕</div>
                                <div className="image-preset-name">Circle</div>
                                <div className="image-preset-desc">300x300px</div>
                            </button>
                            
                            {/* 4:3 Landscape */}
                            <button
                                className="image-preset-btn"
                                onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    onStyleChange({
                                        width: 600,
                                        height: 450,
                                        widthMobile: 400,
                                        heightMobile: 300,
                                        widthType: 'pixel',
                                        heightType: 'pixel',
                                        borderRadius: 8,
                                        borderRadiusMobile: 8,
                                        objectFit: 'cover'
                                    });
                                }}
                                title="4:3 Landscape (600x450px)"
                            >
                                <div className="image-preset-icon">🖼️</div>
                                <div className="image-preset-name">4:3</div>
                                <div className="image-preset-desc">600x450px</div>
                            </button>
                            
                            {/* 16:9 Widescreen */}
                            <button
                                className="image-preset-btn"
                                onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    onStyleChange({
                                        width: 800,
                                        height: 450,
                                        widthMobile: 500,
                                        heightMobile: 281,
                                        widthType: 'pixel',
                                        heightType: 'pixel',
                                        borderRadius: 8,
                                        borderRadiusMobile: 8,
                                        objectFit: 'cover'
                                    });
                                }}
                                title="16:9 Widescreen (800x450px)"
                            >
                                <div className="image-preset-icon">📺</div>
                                <div className="image-preset-name">16:9</div>
                                <div className="image-preset-desc">800x450px</div>
                            </button>
                            
                            {/* Portrait */}
                            <button
                                className="image-preset-btn"
                                onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    onStyleChange({
                                        width: 400,
                                        height: 600,
                                        widthMobile: 300,
                                        heightMobile: 450,
                                        widthType: 'pixel',
                                        heightType: 'pixel',
                                        borderRadius: 8,
                                        borderRadiusMobile: 8,
                                        objectFit: 'cover'
                                    });
                                }}
                                title="Portrait (400x600px)"
                            >
                                <div className="image-preset-icon">📱</div>
                                <div className="image-preset-name">Portrait</div>
                                <div className="image-preset-desc">400x600px</div>
                            </button>
                            
                            {/* Full Width Auto */}
                            <button
                                className="image-preset-btn"
                                onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    onStyleChange({
                                        widthType: 'percent',
                                        width: 100,
                                        widthMobile: 100,
                                        heightType: 'auto',
                                        borderRadius: 0,
                                        borderRadiusMobile: 0,
                                        objectFit: 'cover'
                                    });
                                }}
                                title="Full Width with Auto Height"
                            >
                                <div className="image-preset-icon">↔️</div>
                                <div className="image-preset-name">Full Width</div>
                                <div className="image-preset-desc">100% auto</div>
                            </button>
                            
                            {/* Icon Round */}
                            <button
                                className="image-preset-btn"
                                onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    onStyleChange({
                                        width: 80,
                                        height: 80,
                                        widthMobile: 60,
                                        heightMobile: 60,
                                        widthType: 'pixel',
                                        heightType: 'pixel',
                                        borderRadius: 500,
                                        borderRadiusMobile: 500,
                                        objectFit: 'cover'
                                    });
                                }}
                                title="Small Round Icon (80x80px)"
                            >
                                <div className="image-preset-icon">🔘</div>
                                <div className="image-preset-name">Icon</div>
                                <div className="image-preset-desc">80x80px</div>
                            </button>
                            
                            {/* Thumbnail */}
                            <button
                                className="image-preset-btn"
                                onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    onStyleChange({
                                        width: 150,
                                        height: 150,
                                        widthMobile: 120,
                                        heightMobile: 120,
                                        widthType: 'pixel',
                                        heightType: 'pixel',
                                        borderRadius: 8,
                                        borderRadiusMobile: 8,
                                        objectFit: 'cover'
                                    });
                                }}
                                title="Thumbnail (150x150px)"
                            >
                                <div className="image-preset-icon">🖼️</div>
                                <div className="image-preset-name">Thumbnail</div>
                                <div className="image-preset-desc">150x150px</div>
                            </button>
                        </div>
                        
                        <div className="image-info-text" style={{ marginTop: '12px' }}>
                            <i className="fas fa-info-circle"></i>
                            <span>Presets can be customized after applying</span>
                        </div>
                        
                        <span className="image-section-label">Presets</span>
                    </div>
                )}

                {/* 3️⃣ SIZING SECTION */}
                {activeTab === 'sizing' && (
                    <div className="image-expanded-section">
                        {/* ✅ Desktop/Mobile Toggle - Wie Button Toolbar */}
                        <div className="floating-typo-device-row">
                            <span className="floating-typo-device-label">Desktop</span>
                            <div 
                                className={`floating-typo-toggle ${device === 'mobile' ? 'mobile' : ''}`}
                                onClick={() => setDevice(device === 'desktop' ? 'mobile' : 'desktop')}
                            >
                                <div className="floating-typo-toggle-slider"></div>
                            </div>
                            <span className="floating-typo-device-label">Mobile</span>
                        </div>
                        
                        {/* ✨ ICON MODE TOGGLE - Modern Switch */}
                        <div className="image-mode-toggle-section">
                            <div className="image-mode-header">
                                <div className="image-mode-title">
                                    <i className="fas fa-icons"></i>
                                    <span>Icon Mode</span>
                                </div>
                                <label className="image-modern-toggle">
                                    <input
                                        type="checkbox"
                                        checked={!!(element.iconContainerSize && element.iconContainerSize > 0)}
                                        onChange={(e) => {
                                            // ⚠️ WICHTIG: KEIN preventDefault() bei Checkboxes!
                                            e.stopPropagation();
                                            
                                            const newCheckedState = e.target.checked;
                                            console.log('🔄 Icon Mode Toggle clicked:', {
                                                newCheckedState,
                                                currentIconContainerSize: element.iconContainerSize,
                                                element
                                            });
                                            
                                            if (newCheckedState) {
                                                // ✅ Aktiviere Icon Mode mit optimalen Defaults
                                                const updates = {
                                                    iconContainerSize: 40,
                                                    iconContainerSizeMobile: 36,
                                                    width: 20,
                                                    widthMobile: 20,
                                                    height: 20,
                                                    heightMobile: 20,
                                                    widthType: 'pixel',
                                                    heightType: 'pixel',
                                                    borderRadius: 50,  // Rund!
                                                    containerBgColor: '#667eea',  // Lila
                                                    padding: 0,
                                                    objectFit: 'contain'
                                                };
                                                console.log('✅ Enabling Icon Mode with updates:', updates);
                                                onStyleChange(updates);
                                            } else {
                                                // ✅ Deaktiviere Icon Mode - Zurück zu normalen Defaults
                                                const updates = {
                                                    iconContainerSize: null,  // ✅ null statt 0!
                                                    iconContainerSizeMobile: null,
                                                    borderRadius: 8,
                                                    containerBgColor: 'transparent',
                                                    padding: 0,
                                                    width: 300,
                                                    height: 200,
                                                    widthType: 'pixel',
                                                    heightType: 'pixel'
                                                };
                                                console.log('❌ Disabling Icon Mode with updates:', updates);
                                                onStyleChange(updates);
                                            }
                                        }}
                                        className="image-toggle-input"
                                    />
                                    <span className="image-toggle-slider"></span>
                                </label>
                            </div>
                            <p className="image-mode-description">
                                {element.iconContainerSize > 0 
                                    ? 'Perfect settings for icons with round backgrounds'
                                    : 'Regular image mode with flexible sizing'
                                }
                            </p>
                        </div>
                        
                        {/* ✨ ICON MODE SETTINGS */}
                        {element.iconContainerSize > 0 ? (
                            <>
                                {/* Icon Container Size */}
                                <div className="image-control-group">
                                    <label className="image-control-label">
                                        <i className="fas fa-circle"></i>
                                        Container Size
                                    </label>
                                    <div className="image-slider-container">
                                        <input
                                            type="range"
                                            min="20"
                                            max="100"
                                            step="2"
                                            value={device === 'mobile' ? (element.iconContainerSizeMobile ?? element.iconContainerSize ?? 40) : (element.iconContainerSize ?? 40)}
                                            onChange={(e) => {
                                                e.stopPropagation(); // ✅ Verhindert Toolbar-Close
                                                const prop = device === 'mobile' ? 'iconContainerSizeMobile' : 'iconContainerSize';
                                                onStyleChange({ [prop]: parseInt(e.target.value) });
                                            }}
                                            onClick={(e) => e.stopPropagation()} // ✅ Verhindert Toolbar-Close
                                            className="image-slider"
                                        />
                                        <span className="image-slider-value">
                                            {device === 'mobile' ? (element.iconContainerSizeMobile ?? element.iconContainerSize ?? 40) : (element.iconContainerSize ?? 40)}px
                                        </span>
                                    </div>
                                </div>
                                
                                {/* Icon Size */}
                                <div className="image-control-group">
                                    <label className="image-control-label">
                                        <i className="fas fa-compress-alt"></i>
                                        Icon Size
                                    </label>
                                    <div className="image-slider-container">
                                        <input
                                            type="range"
                                            min="10"
                                            max="80"
                                            step="2"
                                            value={currentWidth}
                                            onChange={(e) => {
                                                const val = parseInt(e.target.value);
                                                onStyleChange({ 
                                                    width: val,
                                                    widthMobile: val,
                                                    height: val,
                                                    heightMobile: val
                                                });
                                            }}
                                            className="image-slider"
                                        />
                                        <span className="image-slider-value">{currentWidth}px</span>
                                    </div>
                                </div>
                                
                                {/* Container Background Color */}
                                <div className="image-control-group">
                                    <label className="image-control-label">
                                        <i className="fas fa-fill-drip"></i>
                                        Container Color
                                    </label>
                                    <div className="image-icon-colors">
                                        {/* Transparent */}
                                        <button
                                            className={`image-color-btn ${element.containerBgColor === 'transparent' ? 'active' : ''}`}
                                            onClick={() => onStyleChange({ containerBgColor: 'transparent' })}
                                            title="Transparent"
                                        >
                                            <div className="image-transparent-pattern"></div>
                                        </button>
                                        
                                        {/* Brand Colors */}
                                        {colors && Object.values(colors).slice(0, 5).map((color, index) => (
                                            <button
                                                key={index}
                                                className={`image-color-btn ${element.containerBgColor === color ? 'active' : ''}`}
                                                style={{ background: color }}
                                                onClick={() => onStyleChange({ containerBgColor: color })}
                                                title={`Brand Color ${index + 1}`}
                                            />
                                        ))}
                                        
                                        {/* Custom Color Picker */}
                                        <button
                                            className="image-color-btn custom"
                                            onClick={() => document.getElementById('icon-custom-color-picker').click()}
                                            title="Custom Color"
                                        >
                                            <i className="fas fa-palette"></i>
                                            <input
                                                id="icon-custom-color-picker"
                                                type="color"
                                                value={element.containerBgColor || '#667eea'}
                                                style={{ display: 'none' }}
                                                onChange={(e) => onStyleChange({ containerBgColor: e.target.value })}
                                            />
                                        </button>
                                    </div>
                                </div>
                                
                                {/* ✨ NEW: Container Border Toggle */}
                                <div className="image-control-group">
                                    <div className="image-mode-header">
                                        <div className="image-mode-title">
                                            <i className="fas fa-border-style"></i>
                                            <span>Container Border</span>
                                        </div>
                                        <label className="image-modern-toggle">
                                            <input
                                                type="checkbox"
                                                checked={element.iconContainerBorderEnabled || false}
                                                onChange={(e) => {
                                                    e.stopPropagation();
                                                    onStyleChange({ 
                                                        iconContainerBorderEnabled: e.target.checked,
                                                        iconContainerBorderWidth: e.target.checked ? 2 : 0,
                                                        iconContainerBorderColor: e.target.checked ? (colors ? Object.values(colors)[0] : '#667eea') : 'transparent'
                                                    });
                                                }}
                                                className="image-toggle-input"
                                            />
                                            <span className="image-toggle-slider"></span>
                                        </label>
                                    </div>
                                </div>
                                
                                {/* ✨ Border Settings (nur wenn aktiviert) */}
                                {element.iconContainerBorderEnabled && (
                                    <>
                                        {/* Border Width */}
                                        <div className="image-control-group">
                                            <label className="image-control-label">
                                                <i className="fas fa-ruler-horizontal"></i>
                                                Border Width
                                            </label>
                                            <div className="image-slider-container">
                                                <input
                                                    type="range"
                                                    min="1"
                                                    max="10"
                                                    step="1"
                                                    value={element.iconContainerBorderWidth || 2}
                                                    onChange={(e) => {
                                                        e.stopPropagation();
                                                        onStyleChange({ iconContainerBorderWidth: parseInt(e.target.value) });
                                                    }}
                                                    onClick={(e) => e.stopPropagation()}
                                                    className="image-slider"
                                                />
                                                <span className="image-slider-value">
                                                    {element.iconContainerBorderWidth || 2}px
                                                </span>
                                            </div>
                                        </div>
                                        
                                        {/* Border Color - Brand Colors */}
                                        <div className="image-control-group">
                                            <label className="image-control-label">
                                                <i className="fas fa-palette"></i>
                                                Border Color
                                            </label>
                                            <div className="image-icon-colors">
                                                {/* Brand Colors */}
                                                {colors && Object.values(colors).slice(0, 5).map((color, index) => (
                                                    <button
                                                        key={index}
                                                        className={`image-color-btn ${element.iconContainerBorderColor === color ? 'active' : ''}`}
                                                        style={{ background: color }}
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            onStyleChange({ iconContainerBorderColor: color });
                                                        }}
                                                        title={`Brand Color ${index + 1}`}
                                                    />
                                                ))}
                                                
                                                {/* Custom Color Picker */}
                                                <button
                                                    className="image-color-btn custom"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        document.getElementById('icon-border-color-picker').click();
                                                    }}
                                                    title="Custom Color"
                                                >
                                                    <i className="fas fa-eyedropper"></i>
                                                    <input
                                                        id="icon-border-color-picker"
                                                        type="color"
                                                        value={element.iconContainerBorderColor || '#667eea'}
                                                        style={{ display: 'none' }}
                                                        onChange={(e) => {
                                                            e.stopPropagation();
                                                            onStyleChange({ iconContainerBorderColor: e.target.value });
                                                        }}
                                                    />
                                                </button>
                                            </div>
                                        </div>
                                    </>
                                )}
                                
                                {/* Info */}
                                <div className="image-info-text">
                                    <i className="fas fa-lightbulb"></i>
                                    <span>Icon ({currentWidth}px) centered in {element.iconContainerSize}px round container</span>
                                </div>
                            </>
                        ) : (
                            <>
                                {/* ✅ REGULAR IMAGE MODE - Original Settings */}
                                
                                {/* Width Type */}
                                <div className="image-control-group">
                                    <label className="image-control-label">
                                        <i className="fas fa-arrows-alt-h"></i>
                                        Width Type
                                    </label>
                                    <div className="image-width-type-buttons">
                                        <button
                                            className={`image-width-type-btn ${element.widthType === 'pixel' || !element.widthType ? 'active' : ''}`}
                                            onClick={() => onStyleChange({ widthType: 'pixel', width: 400 })}
                                        >
                                            <i className="fas fa-ruler-horizontal"></i>
                                            Pixel
                                        </button>
                                        <button
                                            className={`image-width-type-btn ${element.widthType === 'percent' ? 'active' : ''}`}
                                            onClick={() => onStyleChange({ widthType: 'percent', width: 100 })}
                                        >
                                            <i className="fas fa-percentage"></i>
                                            Percent
                                        </button>
                                    </div>
                                </div>
                        
                        {/* Width */}
                        {element.widthType === 'percent' ? (
                            <div className="image-control-group">
                                <label className="image-control-label">
                                    Width
                                </label>
                                <div className="image-slider-container">
                                    <input
                                        type="range"
                                        min="10"
                                        max="100"
                                        value={currentWidth}
                                        onChange={(e) => {
                                            const prop = isMobileLayout ? 'widthMobile' : 'width';
                                            onStyleChange({ [prop]: parseInt(e.target.value) });
                                        }}
                                        className="image-slider"
                                    />
                                    <span className="image-slider-value">{currentWidth}%</span>
                                </div>
                            </div>
                        ) : (
                            <div className="image-control-group">
                                <label className="image-control-label">
                                    Width
                                </label>
                                <div className="image-slider-container">
                                    <input
                                        type="range"
                                        min="1"
                                        max="1000"
                                        value={currentWidth}
                                        onChange={(e) => {
                                            const prop = isMobileLayout ? 'widthMobile' : 'width';
                                            onStyleChange({ [prop]: parseInt(e.target.value) });
                                        }}
                                        className="image-slider"
                                    />
                                    <span className="image-slider-value">{currentWidth}px</span>
                                </div>
                            </div>
                        )}

                        {/* Height Type */}
                        <div className="image-control-group">
                            <label className="image-control-label">
                                <i className="fas fa-arrows-alt-v"></i>
                                Height Type
                            </label>
                            <div className="image-width-type-buttons">
                                <button
                                    className={`image-width-type-btn ${element.heightType === 'pixel' || !element.heightType ? 'active' : ''}`}
                                    onClick={() => onStyleChange({ heightType: 'pixel' })}
                                >
                                    <i className="fas fa-ruler-vertical"></i>
                                    Pixel
                                </button>
                                <button
                                    className={`image-width-type-btn ${element.heightType === 'auto' ? 'active' : ''}`}
                                    onClick={() => onStyleChange({ heightType: 'auto' })}
                                >
                                    <i className="fas fa-magic"></i>
                                    Auto
                                </button>
                            </div>
                        </div>

                        {/* Height (nur bei pixel) - SLIDER */}
                        {element.heightType !== 'auto' && (
                            <div className="image-control-group">
                                <label className="image-control-label">
                                    Height
                                </label>
                                <div className="image-slider-container">
                                    <input
                                        type="range"
                                        min="1"
                                        max="1000"
                                        value={currentHeight}
                                        onChange={(e) => {
                                            const prop = device === 'mobile' ? 'heightMobile' : 'height';
                                            onStyleChange({ [prop]: parseInt(e.target.value) });
                                        }}
                                        className="image-slider"
                                    />
                                    <span className="image-slider-value">{currentHeight}px</span>
                                </div>
                            </div>
                        )}

                        {/* Max Width - SLIDER */}
                        <div className="image-control-group">
                            <label className="image-control-label">
                                Max Width
                            </label>
                            <div className="image-slider-container">
                                <input
                                    type="range"
                                    min="200"
                                    max="2000"
                                    step="50"
                                    value={currentMaxWidth}
                                    onChange={(e) => {
                                        const prop = device === 'mobile' ? 'maxWidthMobile' : 'maxWidth';
                                        onStyleChange({ [prop]: parseInt(e.target.value) });
                                    }}
                                    className="image-slider"
                                />
                                <span className="image-slider-value">{currentMaxWidth}px</span>
                            </div>
                        </div>

                        {/* Info Text */}
                        <div className="image-info-text">
                            <i className="fas fa-info-circle"></i>
                            <span>{device === 'mobile' ? 'Mobile' : 'Desktop'} sizing</span>
                        </div>
                            </>
                        )}
                        
                        <span className="image-section-label">Sizing</span>
                    </div>
                )}

                {/* 3️⃣ STYLING SECTION */}
                {activeTab === 'styling' && (
                    <div className="image-expanded-section">
                        {/* ✅ Desktop/Mobile Toggle */}
                        <div className="floating-typo-device-row">
                            <span className="floating-typo-device-label">Desktop</span>
                            <div 
                                className={`floating-typo-toggle ${device === 'mobile' ? 'mobile' : ''}`}
                                onClick={() => setDevice(device === 'desktop' ? 'mobile' : 'desktop')}
                            >
                                <div className="floating-typo-toggle-slider"></div>
                            </div>
                            <span className="floating-typo-device-label">Mobile</span>
                        </div>
                        
                        {/* Border Radius */}
                        <div className="image-control-group">
                            <label className="image-control-label">
                                <i className="fas fa-border-style"></i>
                                Border Radius
                            </label>
                            <div className="image-slider-container">
                                <input
                                    type="range"
                                    min="0"
                                    max="500"
                                    value={currentBorderRadius}
                                    onChange={(e) => {
                                        const prop = device === 'mobile' ? 'borderRadiusMobile' : 'borderRadius';
                                        onStyleChange({ [prop]: parseInt(e.target.value) });
                                    }}
                                    className="image-slider"
                                />
                                <span className="image-slider-value">{currentBorderRadius}px</span>
                            </div>
                            <p className="image-slider-hint">Tip: Use 500px for perfect circles</p>
                        </div>
                        
                        {/* Padding */}
                        <div className="image-control-group">
                            <label className="image-control-label">
                                <i className="fas fa-compress-arrows-alt"></i>
                                Padding
                            </label>
                            <div className="image-slider-container">
                                <input
                                    type="range"
                                    min="0"
                                    max="80"
                                    value={device === 'mobile' ? 
                                        (element.paddingMobile ?? element.padding ?? 0) : 
                                        (element.padding ?? 0)}
                                    onChange={(e) => {
                                        const prop = device === 'mobile' ? 'paddingMobile' : 'padding';
                                        onStyleChange({ [prop]: parseInt(e.target.value) });
                                    }}
                                    className="image-slider"
                                />
                                <span className="image-slider-value">
                                    {device === 'mobile' ? 
                                        (element.paddingMobile ?? element.padding ?? 0) : 
                                        (element.padding ?? 0)}px
                                </span>
                            </div>
                        </div>
                        
                        {/* Background Color (nur wenn Padding aktiv) */}
                        {(element.padding > 0 || element.paddingMobile > 0) && (
                            <div className="image-control-group">
                                <label className="image-control-label">
                                    <i className="fas fa-fill-drip"></i>
                                    Background
                                </label>
                                <div className="image-color-picker">
                                    <input
                                        type="color"
                                        value={element.containerBgColor || '#ffffff'}
                                        onChange={(e) => onStyleChange({ containerBgColor: e.target.value })}
                                        className="image-color-input"
                                    />
                                    <input
                                        type="text"
                                        value={element.containerBgColor || '#ffffff'}
                                        onChange={(e) => onStyleChange({ containerBgColor: e.target.value })}
                                        className="image-color-text"
                                    />
                                </div>
                            </div>
                        )}

                        {/* Object Fit */}
                        <div className="image-control-group">
                            <label className="image-control-label">
                                <i className="fas fa-crop"></i>
                                Object Fit
                            </label>
                            <select
                                value={element.objectFit || 'cover'}
                                onChange={(e) => onStyleChange({ objectFit: e.target.value })}
                                className="image-select"
                            >
                                <option value="cover">Cover (Fill)</option>
                                <option value="contain">Contain (Fit)</option>
                                <option value="fill">Fill (Stretch)</option>
                                <option value="none">None (Original)</option>
                            </select>
                        </div>

                        {/* Invert Colors Toggle */}
                        <div className="image-control-group">
                            <label className="image-control-label">
                                <i className="fas fa-adjust"></i>
                                Invert Colors
                            </label>
                            <button
                                className={`image-toggle-btn ${element.invertColors ? 'active' : ''}`}
                                onClick={() => onStyleChange({ invertColors: !element.invertColors })}
                                title="Invert image colors (useful for logos)"
                            >
                                <div className="image-toggle-indicator">
                                    <div className={`image-toggle-circle ${element.invertColors ? 'active' : ''}`}></div>
                                </div>
                                <span>{element.invertColors ? 'ON' : 'OFF'}</span>
                            </button>
                            <p className="image-slider-hint">Perfect for inverting logos in dark/light themes</p>
                        </div>

                        {/* Border Toggle */}
                        <div className="image-control-group">
                            <label className="image-control-label">
                                <i className="fas fa-border-all"></i>
                                Border
                            </label>
                            <button
                                className={`image-simple-toggle ${element.borderEnabled ? 'active' : ''}`}
                                onClick={() => onStyleChange({ borderEnabled: !element.borderEnabled })}
                            >
                                <span className="toggle-text">{element.borderEnabled ? 'ON' : 'OFF'}</span>
                            </button>
                        </div>

                        {/* Border Settings */}
                        {element.borderEnabled && (
                            <>
                                <div className="image-control-group">
                                    <label className="image-control-label">
                                        Border Width
                                    </label>
                                    <div className="image-slider-container">
                                        <input
                                            type="range"
                                            min="1"
                                            max="10"
                                            value={element.borderWidth || 2}
                                            onChange={(e) => onStyleChange({ borderWidth: parseInt(e.target.value) })}
                                            className="image-slider"
                                        />
                                        <span className="image-slider-value">{element.borderWidth || 2}px</span>
                                    </div>
                                </div>
                                <div className="image-control-group">
                                    <label className="image-control-label">
                                        Border Color
                                    </label>
                                    <div className="image-color-picker">
                                        <input
                                            type="color"
                                            value={element.borderColor || '#dddddd'}
                                            onChange={(e) => onStyleChange({ borderColor: e.target.value })}
                                            className="image-color-input"
                                        />
                                        <input
                                            type="text"
                                            value={element.borderColor || '#dddddd'}
                                            onChange={(e) => onStyleChange({ borderColor: e.target.value })}
                                            className="image-color-text"
                                        />
                                    </div>
                                </div>
                            </>
                        )}

                        {/* Shadow Toggle */}
                        <div className="image-control-group">
                            <label className="image-control-label">
                                <i className="fas fa-adjust"></i>
                                Shadow
                            </label>
                            <button
                                className={`image-simple-toggle ${element.shadowEnabled ? 'active' : ''}`}
                                onClick={() => onStyleChange({ shadowEnabled: !element.shadowEnabled })}
                            >
                                <span className="toggle-text">{element.shadowEnabled ? 'ON' : 'OFF'}</span>
                            </button>
                        </div>

                        {/* Shadow Settings */}
                        {element.shadowEnabled && (
                            <>
                                <div className="image-control-group">
                                    <label className="image-control-label">
                                        Shadow Intensity
                                    </label>
                                    <div className="image-slider-container">
                                        <input
                                            type="range"
                                            min="5"
                                            max="50"
                                            value={element.shadowIntensity || 20}
                                            onChange={(e) => onStyleChange({ shadowIntensity: parseInt(e.target.value) })}
                                            className="image-slider"
                                        />
                                        <span className="image-slider-value">{element.shadowIntensity || 20}px</span>
                                    </div>
                                </div>
                                <div className="image-control-group">
                                    <label className="image-control-label">
                                        Shadow Distance
                                    </label>
                                    <div className="image-slider-container">
                                        <input
                                            type="range"
                                            min="0"
                                            max="30"
                                            value={element.shadowDistance || 8}
                                            onChange={(e) => onStyleChange({ shadowDistance: parseInt(e.target.value) })}
                                            className="image-slider"
                                        />
                                        <span className="image-slider-value">{element.shadowDistance || 8}px</span>
                                    </div>
                                </div>
                                <div className="image-control-group">
                                    <label className="image-control-label">
                                        Shadow Opacity
                                    </label>
                                    <div className="image-slider-container">
                                        <input
                                            type="range"
                                            min="0"
                                            max="100"
                                            value={element.shadowOpacity || 20}
                                            onChange={(e) => onStyleChange({ shadowOpacity: parseInt(e.target.value) })}
                                            className="image-slider"
                                        />
                                        <span className="image-slider-value">{element.shadowOpacity || 20}%</span>
                                    </div>
                                </div>
                            </>
                        )}
                        
                        <span className="image-section-label">Styling</span>
                    </div>
                )}

                {/* 4️⃣ SPACING SECTION */}
                {activeTab === 'spacing' && (
                    <div className="image-expanded-section">
                        {/* ✅ Desktop/Mobile Toggle */}
                        <div className="floating-typo-device-row">
                            <span className="floating-typo-device-label">Desktop</span>
                            <div 
                                className={`floating-typo-toggle ${device === 'mobile' ? 'mobile' : ''}`}
                                onClick={() => setDevice(device === 'desktop' ? 'mobile' : 'desktop')}
                            >
                                <div className="floating-typo-toggle-slider"></div>
                            </div>
                            <span className="floating-typo-device-label">Mobile</span>
                        </div>
                        
                        {/* Alignment */}
                        <div className="image-control-group">
                            <label className="image-control-label">
                                <i className="fas fa-align-center"></i>
                                Alignment
                            </label>
                            <div className="image-alignment-buttons">
                                <button
                                    className={`image-align-btn ${element.alignment === 'left' || !element.alignment ? 'active' : ''}`}
                                    onClick={() => onStyleChange({ alignment: 'left' })}
                                >
                                    <i className="fas fa-align-left"></i>
                                </button>
                                <button
                                    className={`image-align-btn ${element.alignment === 'center' ? 'active' : ''}`}
                                    onClick={() => onStyleChange({ alignment: 'center' })}
                                >
                                    <i className="fas fa-align-center"></i>
                                </button>
                                <button
                                    className={`image-align-btn ${element.alignment === 'right' ? 'active' : ''}`}
                                    onClick={() => onStyleChange({ alignment: 'right' })}
                                >
                                    <i className="fas fa-align-right"></i>
                                </button>
                            </div>
                        </div>

                        {/* Margin Top */}
                        <div className="image-control-group">
                            <label className="image-control-label">
                                <i className="fas fa-arrow-up"></i>
                                Margin Top
                            </label>
                            <div className="image-slider-container">
                                <input
                                    type="range"
                                    min="0"
                                    max="100"
                                    value={device === 'mobile' ? 
                                        (element.marginTopMobile ?? element.marginTop ?? 0) : 
                                        (element.marginTop ?? 0)}
                                    onChange={(e) => {
                                        const prop = device === 'mobile' ? 'marginTopMobile' : 'marginTop';
                                        onStyleChange({ [prop]: parseInt(e.target.value) });
                                    }}
                                    className="image-slider"
                                />
                                <span className="image-slider-value">
                                    {device === 'mobile' ? 
                                        (element.marginTopMobile ?? element.marginTop ?? 0) : 
                                        (element.marginTop ?? 0)}px
                                </span>
                            </div>
                        </div>

                        {/* Margin Bottom */}
                        <div className="image-control-group">
                            <label className="image-control-label">
                                <i className="fas fa-arrow-down"></i>
                                Margin Bottom
                            </label>
                            <div className="image-slider-container">
                                <input
                                    type="range"
                                    min="0"
                                    max="100"
                                    value={device === 'mobile' ? 
                                        (element.marginBottomMobile ?? element.marginBottom ?? 0) : 
                                        (element.marginBottom ?? 0)}
                                    onChange={(e) => {
                                        const prop = device === 'mobile' ? 'marginBottomMobile' : 'marginBottom';
                                        onStyleChange({ [prop]: parseInt(e.target.value) });
                                    }}
                                    className="image-slider"
                                />
                                <span className="image-slider-value">
                                    {device === 'mobile' ? 
                                        (element.marginBottomMobile ?? element.marginBottom ?? 0) : 
                                        (element.marginBottom ?? 0)}px
                                </span>
                            </div>
                        </div>

                        {/* Info Text */}
                        <div className="image-info-text">
                            <i className="fas fa-info-circle"></i>
                            <span>{device === 'mobile' ? 'Mobile' : 'Desktop'} spacing</span>
                        </div>
                        
                        <span className="image-section-label">Spacing</span>
                    </div>
                )}

                {/* 5️⃣ ANIMATION SECTION */}
                {activeTab === 'animation' && (
                    <div className="image-expanded-section image-animation-section">
                        {/* Animation Toggle */}
                        <div className="image-mode-header">
                            <div className="image-mode-title">
                                <i className="fas fa-play-circle"></i>
                                <span>Animation</span>
                            </div>
                            <label className="image-modern-toggle">
                                <input
                                    type="checkbox"
                                    checked={element.animationEnabled || false}
                                    onChange={(e) => {
                                        e.stopPropagation();
                                        const enabled = e.target.checked;
                                        onStyleChange({ 
                                            animationEnabled: enabled,
                                            triggerAnimation: enabled ? Date.now() : null 
                                        });
                                    }}
                                    className="image-toggle-input"
                                />
                                <span className="image-toggle-slider"></span>
                            </label>
                        </div>

                        {element.animationEnabled && (
                            <>
                                {/* Animation Type Selection */}
                                <div className="image-control-group">
                                    <label className="image-control-label">
                                        <i className="fas fa-wand-magic-sparkles"></i>
                                        Animation Type
                                    </label>
                                    <div className="image-animation-grid">
                                        <button
                                            type="button"
                                            className={`image-animation-selector ${(element.animationType || 'fadeInUp') === 'fadeInUp' ? 'active' : ''}`}
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                onStyleChange({ 
                                                    animationType: 'fadeInUp',
                                                    triggerAnimation: Date.now()
                                                });
                                            }}
                                            title="Fade In Up"
                                        >
                                            <div className="image-animation-icon">↗️</div>
                                            <span>Fade Up</span>
                                        </button>

                                        <button
                                            type="button"
                                            className={`image-animation-selector ${(element.animationType || 'fadeInUp') === 'slideInLeft' ? 'active' : ''}`}
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                onStyleChange({ 
                                                    animationType: 'slideInLeft',
                                                    triggerAnimation: Date.now()
                                                });
                                            }}
                                            title="Slide In Left"
                                        >
                                            <div className="image-animation-icon">➡️</div>
                                            <span>Slide</span>
                                        </button>

                                        <button
                                            type="button"
                                            className={`image-animation-selector ${(element.animationType || 'fadeInUp') === 'fadeIn' ? 'active' : ''}`}
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                onStyleChange({ 
                                                    animationType: 'fadeIn',
                                                    triggerAnimation: Date.now()
                                                });
                                            }}
                                            title="Fade In"
                                        >
                                            <div className="image-animation-icon">✨</div>
                                            <span>Fade</span>
                                        </button>

                                        <button
                                            type="button"
                                            className={`image-animation-selector ${(element.animationType || 'fadeInUp') === 'elegantScale' ? 'active' : ''}`}
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                onStyleChange({ 
                                                    animationType: 'elegantScale',
                                                    triggerAnimation: Date.now()
                                                });
                                            }}
                                            title="Elegant Scale - Premium smooth animation"
                                        >
                                            <div className="image-animation-icon">✨</div>
                                            <span>Elegant</span>
                                        </button>
                                    </div>
                                </div>

                                {/* Animation Speed */}
                                <div className="image-control-group">
                                    <label className="image-control-label">
                                        <i className="fas fa-gauge"></i>
                                        Speed
                                    </label>
                                    <div className="image-speed-buttons">
                                        <button
                                            type="button"
                                            className={`image-speed-selector ${(element.animationSpeed || 'normal') === 'fast' ? 'active' : ''}`}
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                onStyleChange({ 
                                                    animationSpeed: 'fast',
                                                    triggerAnimation: Date.now()
                                                });
                                            }}
                                        >
                                            Fast
                                        </button>
                                        <button
                                            type="button"
                                            className={`image-speed-selector ${(element.animationSpeed || 'normal') === 'normal' ? 'active' : ''}`}
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                onStyleChange({ 
                                                    animationSpeed: 'normal',
                                                    triggerAnimation: Date.now()
                                                });
                                            }}
                                        >
                                            Normal
                                        </button>
                                        <button
                                            type="button"
                                            className={`image-speed-selector ${(element.animationSpeed || 'normal') === 'slow' ? 'active' : ''}`}
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                onStyleChange({ 
                                                    animationSpeed: 'slow',
                                                    triggerAnimation: Date.now()
                                                });
                                            }}
                                        >
                                            Slow
                                        </button>
                                    </div>
                                </div>

                                {/* Preview Button */}
                                <button
                                    type="button"
                                    className="image-animation-preview-btn"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        onStyleChange({ triggerAnimation: Date.now() });
                                    }}
                                >
                                    <i className="fas fa-play"></i> Preview Animation
                                </button>

                                {/* Info */}
                                <div className="image-info-text" style={{ marginTop: '12px' }}>
                                    <i className="fas fa-info-circle"></i>
                                    <span>Animation plays automatically when user scrolls to image</span>
                                </div>
                            </>
                        )}
                        
                        <span className="image-section-label">Animation</span>
                    </div>
                )}
            </div>
        </div>
        </>
    );

    // ✅ Render via Portal
    return ReactDOM.createPortal(renderToolbar(), document.body);
}

export default ImageFloatingToolbar;

